import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Th, Td, Pagination } from "./AdminUsers";
import { Shield, ShieldAlert, ShieldOff, Ban } from "lucide-react";
import { toast } from "sonner";

export default function AdminSecurity() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [kind, setKind] = useState("");
  const [skip, setSkip] = useState(0);
  const [blockIp, setBlockIp] = useState("");
  const limit = 50;

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/security/summary");
      setSummary(data);
    } catch (e) { console.error("loadSummary failed", e); }
  }, []);
  const loadEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/security/events", { params: { kind, skip, limit } });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) { console.error("loadEvents failed", e); }
  }, [kind, skip]);
  useEffect(() => { loadSummary(); loadEvents(); }, [loadSummary, loadEvents]);

  const doBlock = async (ip) => {
    try {
      await api.post("/admin/security/block-ip", { ip });
      toast.success("IP blocked");
      setBlockIp("");
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const doUnblock = async (ip) => {
    try {
      await api.post("/admin/security/unblock-ip", { ip });
      toast.success("IP unblocked");
      loadSummary();
    } catch (e) { console.error("doUnblock failed", e); }
  };

  if (!summary) return <AdminLayout title="Loading…" subtitle="Module 20" />;

  return (
    <AdminLayout subtitle="Module 20" title="Security Center">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 mb-8" data-testid="security-kpis">
        <SecKPI label="Login attempts · 24h" value={summary.last_24h.login_attempts} icon={Shield} />
        <SecKPI label="Failed logins · 24h" value={summary.last_24h.failed_logins} icon={ShieldAlert} danger />
        <SecKPI label="MFA failures · 24h" value={summary.last_24h.mfa_failures} icon={ShieldOff} danger />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Card className="rounded-sm border-white/10 bg-white/[0.02]">
          <CardContent className="p-6">
            <div className="eyebrow text-zinc-500 mb-3">Suspicious IPs · 24h</div>
            {summary.suspicious_ips.length === 0 ? (
              <div className="text-sm text-zinc-500" data-testid="security-suspicious-empty">No IPs over the threshold.</div>
            ) : (
              <ul className="space-y-2" data-testid="security-suspicious-list">
                {summary.suspicious_ips.map((s, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="font-mono-plex">{s.ip}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-zinc-500">{s.fail_count} fails</span>
                      <Button size="sm" variant="outline" className="rounded-sm h-7 border-ec-error/40 text-ec-error gap-1 hover:bg-ec-error/10" onClick={() => doBlock(s.ip)} data-testid={`security-block-${s.ip}`}>
                        <Ban className="w-3 h-3" /> Block
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-sm border-white/10 bg-white/[0.02]">
          <CardContent className="p-6">
            <div className="eyebrow text-zinc-500 mb-3">Blocked IPs</div>
            <div className="flex gap-2 mb-4">
              <Input value={blockIp} onChange={(e) => setBlockIp(e.target.value)} placeholder="e.g. 203.0.113.5" className="rounded-sm h-9 bg-white/5 border-white/10 text-white" data-testid="security-block-input" />
              <Button className="rounded-sm h-9 bg-ec-error hover:bg-ec-error/90 gap-1" onClick={() => blockIp && doBlock(blockIp)} data-testid="security-block-button"><Ban className="w-3 h-3" />Block</Button>
            </div>
            {summary.blocked_ips.length === 0 ? (
              <div className="text-sm text-zinc-500" data-testid="security-blocked-empty">No IPs blocked.</div>
            ) : (
              <ul className="space-y-2" data-testid="security-blocked-list">
                {summary.blocked_ips.map((b) => (
                  <li key={b.ip} className="flex items-center justify-between text-sm">
                    <span className="font-mono-plex">{b.ip}</span>
                    <Button size="sm" variant="outline" className="rounded-sm h-7 border-white/10 text-white hover:bg-white/10" onClick={() => doUnblock(b.ip)} data-testid={`security-unblock-${b.ip}`}>Unblock</Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="security-event-filter">
          <option value="" className="bg-ec-ink">All event types</option>
          <option value="login_success" className="bg-ec-ink">Login success</option>
          <option value="login_failure" className="bg-ec-ink">Login failure</option>
          <option value="mfa_failure" className="bg-ec-ink">MFA failure</option>
        </select>
      </div>

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="security-events-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <Th>Timestamp</Th>
                <Th>Kind</Th>
                <Th>Email</Th>
                <Th>IP</Th>
                <Th>User-Agent</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`security-event-${r.id}`}>
                  <Td className="font-mono-plex text-xs text-zinc-400">{r.created_at}</Td>
                  <Td>{r.kind === "login_failure" || r.kind === "mfa_failure"
                    ? <span className="text-ec-error">{r.kind}</span>
                    : <span className="text-ec-emerald">{r.kind}</span>}
                  </Td>
                  <Td className="text-xs">{r.email || "—"}</Td>
                  <Td className="font-mono-plex text-xs">{r.ip || "—"}</Td>
                  <Td className="text-[10px] text-zinc-500 max-w-[300px] truncate">{r.user_agent || "—"}</Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="p-10 text-center text-zinc-500" data-testid="security-events-empty">No events.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination total={total} skip={skip} limit={limit} setSkip={setSkip} />
    </AdminLayout>
  );
}

function SecKPI({ label, value, icon: Icon, danger }) {
  return (
    <div className={`p-5 bg-ec-ink ${danger && value > 0 ? "border-l-2 border-ec-error" : ""}`} data-testid={`security-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="eyebrow text-zinc-500">{label}</div>
        <Icon className={`w-3.5 h-3.5 ${danger && value > 0 ? "text-ec-error" : "text-ec-emerald"}`} />
      </div>
      <div className="font-display text-3xl font-black mt-2">{value.toLocaleString()}</div>
    </div>
  );
}
